import { useEffect, useState, useRef } from "react";
import { Feature } from "ol";
import { Point } from "ol/geom";
import { fromLonLat, toLonLat } from "ol/proj";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { Draw, Modify, MouseWheelZoom, Snap } from "ol/interaction";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";

const editStyle = new Style({
  image: new CircleStyle({
    radius: 9,
    fill: new Fill({ color: "rgba(245, 158, 11, 0.9)" }),
    stroke: new Stroke({ color: "#FFFFFF", width: 2.5 }),
  }),
});

export const useEditLayer = ({
  map,
  edit = false,
  waypoints = [],
  from,
  to,
  onWaypointChange,
  onAdd,
  onChange,
}) => {
  const [interactions, setInteractions] = useState({});
  const sourceRef = useRef(new VectorSource());
  const layerRef = useRef(null);
  const waypointsRef = useRef(waypoints);
  const fromRef = useRef(from);
  const toRef = useRef(to);

  useEffect(() => {
    waypointsRef.current = waypoints;
  }, [waypoints]);

  useEffect(() => {
    fromRef.current = from;
    toRef.current = to;
  }, [from, to]);

  // Sync features in vector source when waypoints/from/to change
  useEffect(() => {
    const source = sourceRef.current;
    source.clear();

    if (Array.isArray(waypoints) && waypoints.length > 0) {
      waypoints.forEach((wp, index) => {
        if (!wp?.coordinate) return;
        const feature = new Feature({
          geometry: new Point(fromLonLat(wp.coordinate)),
        });
        feature.set("waypointIndex", index);
        feature.set("waypointId", wp.id);
        source.addFeature(feature);
      });
    } else {
      if (from) {
        const fromFeature = new Feature({
          geometry: new Point(fromLonLat(from)),
        });
        fromFeature.set("target", "from");
        source.addFeature(fromFeature);
      }
      if (to) {
        const toFeature = new Feature({
          geometry: new Point(fromLonLat(to)),
        });
        toFeature.set("target", "to");
        source.addFeature(toFeature);
      }
    }
  }, [waypoints, from, to]);

  // Initialize OpenLayers interactions once
  useEffect(() => {
    if (!map) return;
    const source = sourceRef.current;

    const mouseWheelZoom = new MouseWheelZoom();
    map.addInteraction(mouseWheelZoom);
    mouseWheelZoom.setActive(false);

    const modify = new Modify({ source });
    map.addInteraction(modify);
    modify.setActive(false);

    const draw = new Draw({ source, type: "Point" });
    map.addInteraction(draw);
    draw.setActive(false);

    const snap = new Snap({ source });
    map.addInteraction(snap);
    snap.setActive(false);

    const vectorLayer = new VectorLayer({
      source,
      style: editStyle,
      zIndex: 10,
      visible: false,
    });
    layerRef.current = vectorLayer;
    map.addLayer(vectorLayer);

    const handleDrawEnd = (event) => {
      const coord = toLonLat(event.feature.getGeometry().getCoordinates());
      const currentWaypoints = waypointsRef.current;
      if (Array.isArray(currentWaypoints) && currentWaypoints.length > 0) {
        const newWp = {
          id: `wp-${Date.now()}`,
          coordinate: [Math.round(coord[0] * 10000) / 10000, Math.round(coord[1] * 10000) / 10000],
          title: `Stopp ${currentWaypoints.length + 1}`,
        };
        if (onWaypointChange) {
          onWaypointChange([...currentWaypoints, newWp]);
        }
      } else if (onAdd) {
        onAdd(coord);
      }
    };

    const handleModifyEnd = (event) => {
      const currentWaypoints = waypointsRef.current;
      if (Array.isArray(currentWaypoints) && currentWaypoints.length > 0) {
        const updated = [...currentWaypoints];
        event.features.forEach((feature) => {
          const idx = feature.get("waypointIndex");
          if (typeof idx === "number" && updated[idx]) {
            const coord = toLonLat(feature.getGeometry().getCoordinates());
            updated[idx] = {
              ...updated[idx],
              coordinate: [Math.round(coord[0] * 10000) / 10000, Math.round(coord[1] * 10000) / 10000],
            };
          }
        });
        if (onWaypointChange) {
          onWaypointChange(updated);
        }
      } else if (onChange) {
        let newFrom = fromRef.current ? [...fromRef.current] : null;
        let newTo = toRef.current ? [...toRef.current] : null;
        event.features.forEach((feature) => {
          const target = feature.get("target");
          const coord = toLonLat(feature.getGeometry().getCoordinates());
          if (target === "from") {
            newFrom = [coord[0], coord[1]];
          }
          if (target === "to") {
            newTo = [coord[0], coord[1]];
          }
        });
        onChange({ from: newFrom, to: newTo });
      }
    };

    draw.on("drawend", handleDrawEnd);
    modify.on("modifyend", handleModifyEnd);

    setInteractions({
      mouseWheelZoom,
      modify,
      draw,
      snap,
    });

    return () => {
      draw.un("drawend", handleDrawEnd);
      modify.un("modifyend", handleModifyEnd);
      map.removeInteraction(mouseWheelZoom);
      map.removeInteraction(modify);
      map.removeInteraction(draw);
      map.removeInteraction(snap);
      map.removeLayer(vectorLayer);
      layerRef.current = null;
    };
  }, [map, onWaypointChange, onAdd, onChange]);

  // Toggle active status based on edit prop
  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.setVisible(Boolean(edit));
    }
    if (!interactions.modify) return;
    interactions.mouseWheelZoom.setActive(edit);
    interactions.modify.setActive(edit);
    interactions.snap.setActive(edit);
    interactions.draw.setActive(edit);
  }, [edit, interactions]);

  return interactions;
};
